import {unwrapAPIError} from 'utils';

import {requestHandler} from '../HTTP';
import {EndPoints, HttpMethods} from '../HTTP/HTTP.types';

/**
 * Asynchronously retrieves the TikTok authorization URL for authentication.
 *
 * @returns {Promise<{ message: { authUrl: string } }>} A promise containing the response object with the TikTok auth URL.
 * @throws If an error occurs during the request, the promise is rejected with the appropriate error.
 */
const getTikTokAuthUrl = async (
  audioId?: string,
): Promise<{
  message: {authUrl: string};
}> => {
  try {
    const res = await requestHandler<{
      message: {authUrl: string};
    }>({
      method: HttpMethods.GET,
      url: `${EndPoints.GenerateTikTokAuthUrl}?audioId=${audioId}` as unknown as EndPoints,
    });
    return res.data;
  } catch (error) {
    const errorValue = unwrapAPIError(error);
    return Promise.reject(errorValue);
  }
};

/**
 * Synchronizes the TikTok account using the authorization code
 *
 * @param {string} code - The authorization code from TikTok
 * @returns {Promise<any>} A promise containing the response with the updated provider data
 * @throws If an error occurs during the request, the promise is rejected with the appropriate error
 */
const syncTikTokAccount = async (code: string): Promise<any> => {
  try {
    const res = await requestHandler({
      method: HttpMethods.GET,
      url: `${EndPoints.SyncTikTokAccount}?code=${encodeURIComponent(
        code,
      )}` as unknown as EndPoints,
    });
    return res.data;
  } catch (error) {
    const errorValue = unwrapAPIError(error);
    return Promise.reject(errorValue);
  }
};

/**
 * Uploads a video to TikTok
 *
 * @param {string} audioId - The ID of the audio to upload
 * @returns {Promise<any>} A promise containing the response with the upload result
 * @throws If an error occurs during the request, the promise is rejected with the appropriate error
 */
const uploadToTikTok = async (audioId: string): Promise<any> => {
  try {
    const res = await requestHandler({
      method: HttpMethods.POST,
      url: `${EndPoints.UploadToTikTok}?audioId=${audioId}` as unknown as EndPoints,
    });
    return res.data;
  } catch (error) {
    const errorValue = unwrapAPIError(error);
    return Promise.reject(errorValue);
  }
};

export const TikTokService = {
  getTikTokAuthUrl,
  syncTikTokAccount,
  uploadToTikTok,
};
